import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { Heart, Gift, Paperclip, X, UploadSimple, WhatsappLogo } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || (typeof window !== 'undefined' ? localStorage.getItem('referral_code') : null);
  const initialRole = searchParams.get('role') === 'doctor' ? 'doctor' : 'patient';

  const [specialties, setSpecialties] = useState([]);
  const [subspecialties, setSubspecialties] = useState({});

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: initialRole,
    phone: '',
    specialty: '',
    subspecialty: '',
    credentials: '',
    referred_by: refCode || '',
  });
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [certificateFiles, setCertificateFiles] = useState([]);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
      setFormData(prev => ({ ...prev, referred_by: refCode }));
    }
  }, [refCode]);

  useEffect(() => {
    // Fetch the full live list of specialties + subspecialties so doctors can pick any of them
    axios.get(`${API}/specialties`)
      .then(({ data }) => {
        setSpecialties(data.specialties || []);
        setSubspecialties(data.subspecialties || {});
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'specialty') {
      // Reset subspecialty whenever primary specialty changes
      setFormData(prev => ({ ...prev, specialty: value, subspecialty: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCertificateFiles = (e) => {
    const files = Array.from(e.target.files || []);
    // Cap each file at 5MB, max 5 files
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
    if (valid.length < files.length) {
      toast.error('Each certificate must be under 5MB');
    }
    setCertificateFiles(prev => [...prev, ...valid].slice(0, 5));
    e.target.value = '';
  };

  const removeCertificateFile = (idx) => {
    setCertificateFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error('Please agree to the Terms of Use, Privacy Policy, and Medical Disclaimer to continue');
      return;
    }
    setLoading(true);

    const result = await register(formData);

    if (result.success) {
      // Upload any pending doctor certificate files (best-effort)
      if (formData.role === 'doctor' && certificateFiles.length > 0) {
        for (const file of certificateFiles) {
          const fd = new FormData();
          fd.append('file', file);
          try {
            await axios.post(`${API}/doctor/certificates`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
              withCredentials: true,
            });
          } catch (err) {
            console.warn('Certificate upload failed for', file.name, err);
          }
        }
        toast.success(`${certificateFiles.length} certificate(s) uploaded`);
      }

      toast.success('Registration successful!');
      if (formData.referred_by) localStorage.removeItem('referral_code');
      if (formData.role === 'patient') navigate('/patient');
      else if (formData.role === 'doctor') navigate('/doctor');
      else navigate('/');
    } else {
      toast.error(result.error || 'Registration failed');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="register-page">
      <Navbar />
      <div className="flex items-center justify-center min-h-screen pt-16 px-4 py-12">
        <div className="w-full max-w-md">
          <div className="card bg-surface p-8">
            <div className="flex items-center justify-center gap-2 font-spectral text-2xl font-semibold text-primary mb-2">
              <Heart size={32} weight="duotone" />
              SecondDoctorOpinion
            </div>
            <h2 className="text-center font-manrope text-2xl font-bold text-primary mb-6">Create Account</h2>

            {formData.referred_by && (
              <div className="flex items-start gap-2 p-3 mb-4 rounded-lg" style={{ backgroundColor: '#E1EDE5', border: '1px solid #3B6953' }} data-testid="referral-banner">
                <Gift size={20} weight="duotone" className="flex-shrink-0 mt-0.5" style={{ color: '#3B6953' }} />
                <div className="text-sm" style={{ color: '#3B6953' }}>
                  You were referred by code <strong>{formData.referred_by}</strong>. Welcome!
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-secondary mb-1">
                  I am a
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="register-role-select"
                >
                  <option value="patient">Patient</option>
                  <option value="doctor">Doctor/Specialist</option>
                </select>
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-secondary mb-1">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-secondary mb-1">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-secondary mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-secondary mb-1">
                  {formData.role === 'doctor' ? (
                    <span className="inline-flex items-center gap-1">
                      <WhatsappLogo size={14} weight="fill" className="text-[#25D366]" />
                      Phone / WhatsApp Number <span className="text-secondary font-normal">(for case alerts)</span>
                    </span>
                  ) : (
                    <>Phone Number (Optional)</>
                  )}
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  required={formData.role === 'doctor'}
                  data-testid="register-phone-input"
                />
                {formData.role === 'doctor' && (
                  <p className="text-xs text-secondary mt-1">We'll WhatsApp you whenever a case is assigned. Email is required too.</p>
                )}
              </div>

              {formData.role === 'doctor' && (
                <>
                  <div>
                    <label htmlFor="specialty" className="block text-sm font-medium text-secondary mb-1">
                      Primary Specialty
                    </label>
                    <select
                      id="specialty"
                      name="specialty"
                      value={formData.specialty}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      required
                      data-testid="register-specialty-select"
                    >
                      <option value="">Select Specialty</option>
                      {specialties.map((spec) => (
                        <option key={spec} value={spec}>{spec}</option>
                      ))}
                    </select>
                    {specialties.length > 0 && (
                      <p className="text-xs text-secondary mt-1">{specialties.length} specialties available</p>
                    )}
                  </div>

                  {formData.specialty && (subspecialties[formData.specialty] || []).length > 0 && (
                    <div>
                      <label htmlFor="subspecialty" className="block text-sm font-medium text-secondary mb-1">
                        Subspecialty / Super-specialty
                        <span className="text-xs text-secondary font-normal ml-1">(optional)</span>
                      </label>
                      <select
                        id="subspecialty"
                        name="subspecialty"
                        value={formData.subspecialty}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        data-testid="register-subspecialty-select"
                      >
                        <option value="">Generalist in {formData.specialty}</option>
                        {(subspecialties[formData.specialty] || []).map((sub) => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label htmlFor="credentials" className="block text-sm font-medium text-secondary mb-1">
                      Credentials
                    </label>
                    <input
                      id="credentials"
                      name="credentials"
                      type="text"
                      value={formData.credentials}
                      onChange={handleChange}
                      placeholder="e.g., MD, DM (Cardiology)"
                      className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="register-credentials-input"
                    />
                  </div>

                  {/* Certificate / Degree Uploads (optional) */}
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1 flex items-center gap-2">
                      <Paperclip size={14} weight="bold" />
                      Upload Certificates <span className="text-xs text-secondary font-normal">(MCI registration, MBBS / MD / DM / fellowship — optional)</span>
                    </label>
                    <label
                      htmlFor="cert-file-input"
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-primary/30 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                      data-testid="cert-dropzone"
                    >
                      <UploadSimple size={18} weight="bold" className="text-primary" />
                      <span className="text-sm text-primary font-medium">
                        {certificateFiles.length > 0 ? 'Add more files' : 'Click to attach certificates (PDF / JPG / PNG, max 5MB each)'}
                      </span>
                      <input
                        id="cert-file-input"
                        type="file"
                        multiple
                        accept="image/*,application/pdf"
                        onChange={handleCertificateFiles}
                        className="hidden"
                        data-testid="cert-file-input"
                      />
                    </label>
                    {certificateFiles.length > 0 && (
                      <div className="mt-2 space-y-2" data-testid="cert-file-list">
                        {certificateFiles.map((f, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-3 px-3 py-2 bg-surface-secondary rounded-lg">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Paperclip size={14} weight="bold" className="text-primary flex-shrink-0" />
                              <span className="text-sm text-primary truncate">{f.name}</span>
                              <span className="text-xs text-secondary flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeCertificateFile(idx)}
                              className="text-secondary hover:text-red-600"
                              data-testid={`remove-cert-${idx}`}
                              aria-label="Remove certificate"
                            >
                              <X size={14} weight="bold" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-secondary mt-2">Our team reviews certificates before activating your specialist profile. Up to 5 files.</p>
                  </div>
                </>
              )}

              <div className="flex items-start gap-2 mt-4" data-testid="register-consent-row">
                <input
                  id="consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 flex-shrink-0 w-4 h-4 accent-current"
                  style={{ accentColor: '#4A6750' }}
                  data-testid="register-consent-checkbox"
                />
                <label htmlFor="consent" className="text-xs text-secondary leading-relaxed cursor-pointer">
                  I have read and agree to the{' '}
                  <Link to="/terms-of-use" target="_blank" className="text-primary font-semibold hover:underline">Terms of Use</Link>,{' '}
                  <Link to="/privacy-policy" target="_blank" className="text-primary font-semibold hover:underline">Privacy Policy</Link>, and{' '}
                  <Link to="/medical-disclaimer" target="_blank" className="text-primary font-semibold hover:underline">Medical Disclaimer</Link>. I understand that a second opinion is not a substitute for in-person consultation with my treating physician.
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit-button"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-secondary">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="register-login-link">
                Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}